/* eslint-disable no-underscore-dangle */
/* eslint-disable no-useless-escape */
/* eslint-disable no-await-in-loop */
import { cloneDeep } from "lodash"
import { Action } from "../models/action"
import ActionType from "../models/actionType"
import DynamicParamsService from "./dynamicParams"
import StructUtils from "../utils/structUtils"
import { Service } from "./Service"
import StoreService from "./store"

class ActionService extends Service {
    /**
     * Execute cette actions si c'est une erreur
     * @param action actions à exec si c'est une erreur
     * @param err objet de l'erreur
     * @returns void
     */
    private static async execError(action: Action, err: unknown) {
        const { actionParams } = action
        if (
            action.actionType === ActionType.ERROR &&
            ((actionParams?.params && actionParams?.functionKey) ||
                actionParams?.conditionParams)
        ) {
            // si erreur conditionnel
            if (actionParams?.conditionParams) {
                // on change ERROR par CONDITION pour laisser
                // le handler CONDITION executer les actions d'erreur
                const condAction = {
                    ...action,
                    actionType: ActionType.CONDITION,
                }
                // en context on met l'erreur
                await this.exec([condAction], err)
            }
            if (actionParams.functionKey) {
                const { source, endPoint } = StoreService.getSourceEndPoint(
                    actionParams.functionKey
                )

                const params = actionParams?.params
                    ? await DynamicParamsService.resolve(
                          actionParams.params,
                          err
                      )
                    : []

                /** /!\ tous les endpoint appelé dans les callback d'action doivent retourner des Promise */
                if (source && endPoint) {
                    await endPoint.apply(source, params)
                }
            }

            return null
        }
        return null
    }

    /**
     * execGlobalErrors Execute toutes les actions de type error
     * après une erreur globale (throw) dans le fils des actions
     * c'est action peuvent être async ou non
     * @param actions action à executer
     * @param err à mettre dans le context
     */
    public static async execGlobalErrors(
        actions: Array<Action>,
        err: unknown
    ): Promise<any> {
        try {
            if (actions && actions.length) {
                for (let index = 0; index < actions.length; index += 1) {
                    const action: Action = actions[index]
                    await this.execError(action, err)
                }
            }
            return null
        } catch (e: unknown) {
            console.error(e)
            return e
        }
    }

    /**
     * exec Execute toute les actions qui sont passées en paramètre
     * c'est action peuvent être async ou non
     * @param actions action à executer
     * @param context context d'exection de l'action (ex. le client conserné par l'action)
     */
    public static async exec(
        actions: Array<Action>,
        context?: unknown
    ): Promise<any> {
        try {
            let result
            if (actions && actions.length) {
                let index = 0
                let block: boolean | undefined = false
                while (index < actions.length && !block) {
                    const action = actions[index] || {}
                    index += 1
                    const { actionParams } = action
                    block = action.actionParams?.block
                    if (
                        action.actionType === ActionType.ROUTING &&
                        actionParams?.to
                    ) {
                        /**
                         * ROUTING
                         */
                        const clonedActionParams: any = cloneDeep(actionParams)
                        // est ce que la route qu'on doit charger a des paramètres
                        if (clonedActionParams && clonedActionParams.to) {
                            if (clonedActionParams.navParam) {
                                const keys = Object.keys(
                                    clonedActionParams.navParam
                                )

                                for (let i = 0; i < keys.length; i += 1) {
                                    const urlParam: string =
                                        clonedActionParams.navParam[keys[i]]
                                    // résolution des params
                                    const resolvedParam: any =
                                        await DynamicParamsService.resolve(
                                            [urlParam],
                                            context
                                        )
                                    const [resolvedValue] = resolvedParam
                                    clonedActionParams.navParam[keys[i]] =
                                        resolvedValue
                                }
                            }

                            const resolvedToParam: any =
                                await DynamicParamsService.resolve(
                                    [clonedActionParams.to],
                                    context
                                )
                            const [resolvedToValue] = resolvedToParam
                            clonedActionParams.to = resolvedToValue
                        }

                        // si oldState est true, garder les anciens navParam + merger les nouveaux
                        if (clonedActionParams?.oldState) {
                            const isObject = (v: any) =>
                                v && typeof v === "object" && !Array.isArray(v)

                            const backState =
                                await StoreService.getInstance().get(
                                    "NavigationService.getBackState",
                                    []
                                )
                            const previousParams =
                                backState && isObject(backState.params)
                                    ? backState.params
                                    : {}
                            const currentParams = isObject(
                                clonedActionParams.navParam
                            )
                                ? clonedActionParams.navParam
                                : {}

                            clonedActionParams.navParam = {
                                ...previousParams,
                                ...currentParams,
                            }
                        }

                        // routing vers la nouvelle page a l'aide du store pour
                        // ne pas avoir de dépendences circulaires
                        StoreService.getInstance().get(
                            "NavigationService.navigateTo",
                            [clonedActionParams.to, clonedActionParams.navParam]
                        )
                    } else if (
                        action.actionType === ActionType.CALLBACK &&
                        actionParams?.functionKey &&
                        actionParams?.params
                    ) {
                        /**
                         * CALLBACK
                         */
                        const { source, endPoint } =
                            StoreService.getSourceEndPoint(
                                actionParams.functionKey
                            )

                        const params = await DynamicParamsService.resolve(
                            actionParams.params,
                            context
                        )

                        /** /!\ tous les endpoint appelés dans les callback d'action doivent retourner des Promise */
                        if (source && endPoint) {
                            result = await endPoint.apply(source, params)
                        }
                    } else if (
                        action.actionType === ActionType.CONDITION &&
                        actionParams?.conditionParams &&
                        actionParams.conditionParams.value
                    ) {
                        // on resolve la valeur à tester
                        const resolvedValue =
                            await DynamicParamsService.resolve(
                                [actionParams.conditionParams.value],
                                context
                            )
                        const value: any = resolvedValue.length
                            ? resolvedValue[0]
                            : ""

                        /**
                         * Condition type CASE avec valeurs simple
                         */
                        if (
                            actionParams.conditionParams.cases &&
                            actionParams.conditionParams.cases.length
                        ) {
                            // on passe dans tous les cas possible pour voir si la condition est vraie
                            for (
                                let i = 0;
                                i < actionParams.conditionParams.cases.length;
                                i += 1
                            ) {
                                const currentCase: any =
                                    actionParams.conditionParams.cases[i]
                                // le prédicate est le type de test qui va etre fait
                                let predicate: "value" | "gt" | "lt" | "ne" =
                                    "value"

                                // un seul prédicate est autorisé
                                if (typeof currentCase.gt !== "undefined") {
                                    predicate = "gt"
                                } else if (
                                    typeof currentCase.lt !== "undefined"
                                ) {
                                    predicate = "lt"
                                } else if (
                                    typeof currentCase.ne !== "undefined"
                                ) {
                                    predicate = "ne"
                                }

                                // on resolve la valeur à tester
                                const resolvedCurrentCaseValue =
                                    await DynamicParamsService.resolve(
                                        [currentCase[predicate]],
                                        context
                                    )
                                const currentCaseValue: any =
                                    resolvedCurrentCaseValue.length
                                        ? resolvedCurrentCaseValue[0]
                                        : ""

                                // si condition vraie ou case défault
                                if (
                                    (predicate === "value" &&
                                        currentCaseValue === value) ||
                                    (predicate === "gt" &&
                                        value > currentCaseValue) ||
                                    (predicate === "lt" &&
                                        value < currentCaseValue) ||
                                    (predicate === "ne" &&
                                        value !== currentCaseValue) ||
                                    currentCaseValue === "$$default"
                                ) {
                                    // on exec la condition qui est vraie ou default
                                    result = await ActionService.exec(
                                        currentCase.actions,
                                        context
                                    )
                                    break
                                }
                            }
                            /**
                             * Condition type IN appliqué sur une liste
                             */
                        } else if (
                            actionParams.conditionParams.in &&
                            actionParams.conditionParams.in.table &&
                            actionParams.conditionParams.in.actions
                        ) {
                            // on resolve la valeur à tester
                            const resolvedTableValue =
                                await DynamicParamsService.resolve(
                                    [actionParams.conditionParams.in.table],
                                    context
                                )
                            const table: any = resolvedTableValue.length
                                ? resolvedTableValue[0]
                                : []

                            const arrayValue = table as Array<any>

                            const findIndex = arrayValue?.length
                                ? StructUtils.findMultiTypes(value, arrayValue)
                                : -1

                            if (findIndex > -1) {
                                // on exec la condition qui est vraie ou default
                                result = await ActionService.exec(
                                    actionParams.conditionParams.in.actions,
                                    context
                                )
                            } else if (
                                actionParams.conditionParams.in.elseActions
                                    ?.length
                            ) {
                                result = await ActionService.exec(
                                    actionParams.conditionParams.in.elseActions,
                                    context
                                )
                            }
                        }
                    } else if (
                        action.actionType === ActionType.CONFIG &&
                        actionParams?.config
                    ) {
                        // wrap et retourne la nouvelle config
                        result = {
                            type: "config",
                            value: actionParams.config,
                        }
                    } else if (
                        action.actionType === ActionType.ERROR &&
                        actionParams?.customError
                    ) {
                        // Erreur custom exec immediatement
                        await this.execError(action, null)
                    }
                }
            }
            return result
        } catch (err: unknown) {
            await this.execGlobalErrors(actions, err)
            console.error(err)
            return err
        }
    }
}

export default ActionService
